/**
 * DriveZone — Subscribe Page
 * Route: /subscribe  (protected)
 */

import { useNavigate } from 'react-router-dom'
import { MapPin, Check, Crown, MessageCircle, Copy, CheckCheck } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useState } from 'react'

const FEATURES = [
  'Unlimited proximity searches',
  'Unlimited distance confirmations',
  'All US cities and towns covered',
  'Results in seconds with smart caching',
  '30 days unlimited access',
]

const PAYBILL  = '0748272022'
const WHATSAPP = '+254748272022'

export default function SubscribePage() {
  const navigate     = useNavigate()
  const { user }     = useAuthStore()
  const isSubscribed = user?.is_subscribed

  const [copied, setCopied] = useState(false)

  function copyPaybill() {
    navigator.clipboard.writeText(PAYBILL).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const whatsappMsg = encodeURIComponent(
    `Hi, I'd like to subscribe to DriveZone Pro. My email: ${user?.email ?? ''}. Please add me to Pro so I can confirm before paying KES 300.`
  )

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-5">

        <button
          onClick={() => navigate('/app/search')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          ← Back to search
        </button>

        {isSubscribed && (
          <div className="card p-5 bg-emerald-50 border border-emerald-200 max-w-md">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">You're already subscribed!</p>
                <p className="text-xs text-emerald-600">DriveZone Pro is active on your account.</p>
              </div>
            </div>
            <button onClick={() => navigate('/account')} className="btn-secondary w-full mt-3 text-sm">
              View account
            </button>
          </div>
        )}

        {!isSubscribed && (
          <div className="grid md:grid-cols-2 gap-4">

            {/* ── Left card: plan details ── */}
            <div className="card p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">DriveZone Pro</h1>
                  <p className="text-sm text-slate-500">Unlimited searches</p>
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-4 text-center">
                <div className="flex items-end justify-center gap-1">
                  <span className="text-4xl font-bold text-slate-900">KES 300</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">per month · pay via M-Pesa</p>
              </div>

              <ul className="space-y-2">
                {FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </span>
                    <span className="text-sm text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* ── Right card: trust-building payment flow ── */}
            <div className="card p-6 flex flex-col gap-5">
              <div>
                <h2 className="text-base font-semibold text-slate-900">How to subscribe</h2>
                <p className="text-xs text-slate-500 mt-0.5">We add you first — then you pay</p>
              </div>

              {/* Step 1 */}
              <div className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-bold
                                 flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">Message us on WhatsApp</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Tell us your email and ask to be added to Pro. We'll activate your account first.
                  </p>
                  <a
                    href={`https://wa.me/${WHATSAPP.replace('+', '')}?text=${whatsappMsg}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600
                               text-white text-sm font-semibold rounded-lg px-4 py-2.5
                               transition-colors w-full justify-center"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp {WHATSAPP}
                  </a>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-bold
                                 flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <div>
                  <p className="text-sm font-medium text-slate-800">Confirm Pro in your account</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Check your{' '}
                    <button
                      onClick={() => navigate('/account')}
                      className="text-primary-600 underline"
                    >
                      account page
                    </button>
                    {' '}— once you see <strong>Pro</strong>, we've added you.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-bold
                                 flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">Then send KES 300 via M-Pesa</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Only after you've confirmed Pro is active on your account.
                  </p>
                  <div className="flex items-center gap-2 mt-2 bg-slate-50 border border-slate-200
                                  rounded-lg px-3 py-2">
                    <span className="text-sm font-mono font-semibold text-slate-900 flex-1">{PAYBILL}</span>
                    <button
                      onClick={copyPaybill}
                      className="text-primary-600 hover:text-primary-700 transition-colors flex-shrink-0"
                      title="Copy number"
                    >
                      {copied
                        ? <CheckCheck className="w-4 h-4 text-emerald-500" />
                        : <Copy className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-400 text-center border-t border-slate-100 pt-3">
                You can verify Pro is active before sending any money.
              </p>
            </div>

          </div>
        )}
      </div>
    </div>
  )
}
